import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

const MembershipSection = ({
  selectedMembership,
  setSelectedMembership,
  onSave,
}) => {
  const [isFirstDropdownOpen, setIsFirstDropdownOpen] = useState(false);
  const [isSecondDropdownOpen, setIsSecondDropdownOpen] = useState(false);
  const [firstDropdownValue, setFirstDropdownValue] = useState("XXXXXX");
  const [secondDropdownValue, setSecondDropdownValue] = useState("XXXXXX");
  const [customMembership, setCustomMembership] = useState("");
  // Scroll related states for both dropdowns
  const firstContentRef = useRef(null);
  const secondContentRef = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [thumbHeight, setThumbHeight] = useState(40);
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [startScrollTop, setStartScrollTop] = useState(0);
  const thumbRef = useRef(null);

  const firstDropdownOptions = [
    "Option 1: Agriculture Group",
    "Option 2: Environmental Club",
    "Option 3: Farmers Association",
    "Option 4: Local Cooperative",
    "Option 5: Community Outreach",
  ];

  const secondDropdownOptions = [
    "Association (ASS)",
    "Alliance (ALL)",
    "Community (COM)",
    "Cooperative (COP)",
    "Chama (CHA)",
    "Groups (GRO)",
    "Membership (MEM)",
    "Savings & Credit (SAC)",
    "Stokvel (STO)",
    "Union (UN)"
  ];

  // Common scroll handling functions
  const handleScroll = (contentRef) => {
    const content = contentRef.current;
    if (!content) return;

    const { scrollTop, scrollHeight, clientHeight } = content;
    const maxScrollTop = scrollHeight - clientHeight;
    const scrollRatio = scrollTop / maxScrollTop;
    const trackHeight = content.clientHeight - thumbHeight;
    setScrollTop(scrollRatio * trackHeight);
  };

  const handleThumbMouseDown = (e, contentRef) => {
    e.preventDefault();
    e.stopPropagation();

    setIsDragging(true);
    setStartY(e.clientY);
    setStartScrollTop(contentRef.current.scrollTop);

    document.addEventListener("mousemove", (e) => handleThumbMouseMove(e, contentRef));
    document.addEventListener("mouseup", () => handleThumbMouseUp(contentRef));
    document.body.style.cursor = "grabbing";
  };

  const handleThumbMouseMove = (e, contentRef) => {
    if (!isDragging) return;

    const deltaY = e.clientY - startY;
    const content = contentRef.current;
    const { scrollHeight, clientHeight } = content;
    const maxScrollTop = scrollHeight - clientHeight;
    const trackHeight = clientHeight - thumbHeight;

    const scrollDelta = (deltaY / trackHeight) * maxScrollTop;
    const newScrollTop = Math.min(
      Math.max(0, startScrollTop + scrollDelta),
      maxScrollTop
    );

    content.scrollTop = newScrollTop;
  };

  const handleThumbMouseUp = (contentRef) => {
    setIsDragging(false);
    document.removeEventListener("mousemove", (e) => handleThumbMouseMove(e, contentRef));
    document.removeEventListener("mouseup", () => handleThumbMouseUp(contentRef));
    document.body.style.cursor = "";
  };

  const handleTrackClick = (e, contentRef) => {
    const content = contentRef.current;
    if (!content || e.target === thumbRef.current) return;

    const track = e.currentTarget;
    const trackRect = track.getBoundingClientRect();
    const clickPosition = e.clientY - trackRect.top;
    const thumbCenter = thumbHeight / 2;
    const scrollRatio =
      (clickPosition - thumbCenter) / (trackRect.height - thumbHeight);

    const { scrollHeight, clientHeight } = content;
    const maxScrollTop = scrollHeight - clientHeight;
    content.scrollTop = scrollRatio * maxScrollTop;
  };

  const handleButtonScroll = (direction, contentRef) => {
    if (contentRef.current) {
      contentRef.current.scrollBy({
        top: direction === 'up' ? -50 : 50,
        behavior: 'smooth'
      });
    }
  };

  const handleFirstDropdownSelect = (value) => {
    setFirstDropdownValue(value);
    setIsFirstDropdownOpen(false);
    setSelectedMembership(value);
    if (onSave) {
      onSave({ type: 'selected', value }); // Pass selected membership
    }
  };
  
  const handleSecondDropdownSelect = (item) => {
    setSecondDropdownValue(item);
    setSelectedMembership(item);
    if (onSave) {
      onSave({ 
        type: 'selected', 
        value: item,
      });
    }
  };
  
  const handleCustomMembershipChange = (e) => {
    const value = e.target.value;
    setCustomMembership(value);
    if (onSave) {
      onSave({ 
        type: 'custom',
        value 
      });
    }
  };
  useEffect(() => {
    // Call onSave whenever membership data changes
    onSave({
      selected: selectedMembership,
      custom: customMembership
    });
  }, [selectedMembership, customMembership, onSave]);

  const handleSave = () => {
    console.log("Membership data:", {
      selected: selectedMembership,
      custom: customMembership
    });
    onSave({
      selected: selectedMembership,
      custom: customMembership
    });
  };
  return (
    <div className="space-y-2 md:mt-12 mt-4">
      {/* Heading */}
    
      <div className="md:h-2"></div>
      
      {/* First Box with Dropdown */}
      <div className="flex flex-col space-y-2">
        <div
          className={`relative md:h-[71px] h-[20px] flex items-center justify-between px-4 rounded-[5px] cursor-pointer ${
            selectedMembership === firstDropdownValue ? "bg-[#2E453D] text-white" : "bg-[#2E453D] text-white"
          }`}
          onClick={() => setIsSecondDropdownOpen(!isSecondDropdownOpen)}
        >
          <span className="font-bold md:pl-6 text-[6px] md:text-[18px]">{secondDropdownValue}</span>
          <div
            className="absolute top-0 right-0 w-[20px] h-[20px] md:h-full md:w-[85px] bg-[#152C24] text-white flex justify-center items-center rounded-r-[5px] cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              setIsSecondDropdownOpen(!isSecondDropdownOpen);
            }}
          >
            <img
              src="https://res.cloudinary.com/dzngpgki4/image/upload/v1746721481/Expand_down_1_fv91ph.png"
              alt="expand"
              className="w-2 h-2 md:w-9 md:h-[35px]"
            />
          </div>
        </div>
        <p className="md:text-[24px] text-[8px] italic font-normal">Select organization groups or affiliations you are a member of, or are involved with:</p>
        
           {/* Second Dropdown Menu */}
           <AnimatePresence>
          {isSecondDropdownOpen && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="relative z-50"
            >
              <div className="md:w-[280px] w-[] border-[1px] border-[#979797]/80 bg-white rounded-[1px] shadow-lg p-2">
                <div className="w-full flex justify-end py-1">
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleButtonScroll('up', secondContentRef);
                    }}
                    className="p-1"
                  >
                    <img 
                      src="https://res.cloudinary.com/dzngpgki4/image/upload/v1747461105/Expand_up_mflvtu.png" 
                      alt="Scroll up" 
                      className="w-3 h-3"
                    />
                  </button>
                </div>

                <div className="custom-scroll-area">
                  <ul
                    className="custom-scroll-content"
                    ref={secondContentRef}
                    onScroll={() => handleScroll(secondContentRef)}
                  >
                    <li className="flex items-center justify-between px-2 md:mt-0  md:py-1">
                      <span className="text-[6px] md:text-[16px] font-bold">
                        Memberships, Affiliations, Associations & Groups
                      </span>
                      <img
                        src="https://res.cloudinary.com/dzngpgki4/image/upload/v1745670022/Arhive_load_duotone_line_c6pcpp.png"
                        alt="icon"
                        className="md:w-[30px] md:h-[30px] h-[7px] w-[7px] cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsSecondDropdownOpen(false);
                        }}
                      />
                    </li>
                    {secondDropdownOptions.map((item, index) => (
                      <li
                        key={`second-${index}`}
                        className={`flex items-start justify-between md:px-3 px-1 md:py-2 py-0 md:mt-1 rounded-md hover:bg-gray-100 cursor-pointer ${
                          secondDropdownValue === item ? "bg-[#E7EBEB]" : ""
                        }`}
                        onClick={() => handleSecondDropdownSelect(item)}
                      >
                        <div className="md:w-[190px]">
                          <span className="text-black text-[4px] md:text-[16px]">
                            {item}
                          </span>
                        </div>
                        <div
                          className={`w-2 h-2 md:w-5 md:h-5 rounded-full ${
                            secondDropdownValue === item ? "bg-[#152C24]" : "bg-gray-200"
                          }`}
                        ></div>
                      </li>
                    ))}
                  </ul>
                  <div className="custom-scroll-track" onClick={(e) => handleTrackClick(e, secondContentRef)}>
                    <div
                      ref={thumbRef}
                      className="custom-scroll-thumb"
                      style={{
                        height: `${thumbHeight}px`,
                        top: `${scrollTop}px`,
                        cursor: isDragging ? "grabbing" : "grab",
                      }}
                      onMouseDown={(e) => handleThumbMouseDown(e, secondContentRef)}
                    ></div>
                  </div>
                </div>

                <div className="w-full flex justify-end py-1">
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleButtonScroll('down', secondContentRef);
                    }}
                    className="p-1"
                  >
                    <img 
                      src="https://res.cloudinary.com/dzngpgki4/image/upload/v1747461085/Expand_down_vbf4bb.png" 
                      alt="Scroll down" 
                      className="w-3 h-3"
                    />
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        {/* Second Box with Dropdown */}
        <div
          className={`relative md:h-[71px] h-[20px] flex items-center justify-between px-4 rounded-[5px] cursor-pointer bg-[#E7EBEB]`}
       
        >
       <input 
  placeholder="XXXXXXXXX XXX" 
  className="font-bold placeholder-black text-black bg-[#E7EBEB] outline-none md:pl-6 text-[6px] md:text-[18px]"
  value={customMembership}
  onChange={handleCustomMembershipChange} // Call handler on change
/>
  
        </div>
        <p className="md:text-[24px] text-[8px] italic font-normal">Type organization groups or affiliations you are a member of, or are involved with:</p>
        

      </div>

      <style jsx>{`
        .custom-scroll-area {
          position: relative;
          max-height: 200px;
          display: flex;
          overflow: hidden;
        }

        .custom-scroll-content {
          flex: 1;
          overflow-y: scroll;
          scrollbar-width: none;
          -ms-overflow-style: none;
          padding-right: 4px;
        }

        .custom-scroll-content::-webkit-scrollbar {
          display: none;
        }

        .custom-scroll-track {
          width: 14px;
          background: #e7ebeb;
          border-radius: 3px;
          position: relative;
          margin-left: 4px;
          cursor: pointer;
        }

        .custom-scroll-thumb {
          width: 100%;
          background: #979797;
          border-radius: 3px;
          position: absolute;
          right: 0;
          transition: background 0.2s;
        }

        .custom-scroll-thumb:hover {
          background: #444;
        }

        @media (max-width: 768px) {
          .custom-scroll-track {
            width: 4px;
            margin-left: 2px;
          }
          .custom-scroll-area {
            position: relative;
            max-height: 150px;
            display: flex;
            overflow: hidden;
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default MembershipSection;